import { Component, Input, OnInit } from "@angular/core";
import { Rodeo } from "../../../../../model/rodeo";
import { RodeoEvent } from "../../../../../model/rodeoEvent";
import { RodeoEventSchedule } from "../../../../../model/rodeoEventSchedule";
import { PayoutConfig } from "../../../../../model/payoutConfig";
import { PointSeries } from "../../../../../model/point-series";
import { LoadingController, NavController, NavParams } from "ionic-angular";
import { PointStatsApiService } from "../../../../../services/points/pointStats.api.service";
import { PointStatListItem } from "../../../../../lib/types/PointStatListItem";
import { getRunSecondValue, groupBy } from "../../../../../lib/helpers";
import { ReportsService } from "../../../../../services/reports.service";

@Component({
    selector: 'page-point-stats-report',
    templateUrl: 'point-stats-report.component.html',
})
export class PointStatsReportComponent implements OnInit {
    @Input('rodeo') rodeo: Rodeo;

    selectedRodeoEvent: RodeoEvent;
    selectedPayout: PayoutConfig;
    assignedPointSeries: PointSeries[];
    selectedPointSeries: PointSeries;
    pointStats: PointStatListItem[];
    runsWithoutPoints: PointStatListItem[];
    payoutSelections: PayoutConfig[];
    groupedPointStats: { [id: string]: PointStatListItem[] } = {};
    printing: boolean;
    includeRunsWithoutPoints: boolean

    constructor(
        private loadingController: LoadingController,
        private pointStatsApiService: PointStatsApiService,
        private navController: NavController,
        private reportService: ReportsService,
        navParams: NavParams
    ) {
        if (!this.rodeo)
            this.rodeo = navParams.get('rodeo');

        this.selectedRodeoEvent = navParams.get('selectedRodeoEvent');
        this.selectedPayout = navParams.get('selectedPayout');
        this.selectedPointSeries = navParams.get('selectedPointSeries');
        this.printing = navParams.get('print');
        this.includeRunsWithoutPoints = navParams.get('includeRunsWithoutPoints');
        if (this.printing) {
            this.loadPointSeries();
            this.reloadPointStats();
        }
    }


    async ngOnInit() {
        if (!this.printing) {
            this.selectedRodeoEvent = this.rodeo.rodeoEvents[0];
            // this.payoutSelections = [{ name: 'All', id: 0 } as PayoutConfig, ...this.rodeo.payoutConfigs]
            // this.selectedPayout = this.payoutSelections[0];
            this.updatePayouts();
            this.loadPointSeries();
            await this.reloadPointStats();
        }
    }

    loadPointSeries() {
        const useAllPayout = this.selectedPayout.id === 0;

        const allPointSeriesInPayouts = useAllPayout ? this.rodeo.payoutConfigs.flatMap(pc => pc.pointSeries) : this.selectedPayout.pointSeries;

        if (allPointSeriesInPayouts && allPointSeriesInPayouts.length > 0) {
            const pointSeriesMap = new Map(allPointSeriesInPayouts.map<[any, any]>(item =>
                [item['id'], item]))
            const distinctPointSeries = Array.from<PointSeries>(pointSeriesMap.values());


            this.assignedPointSeries = distinctPointSeries;
            this.selectedPointSeries = this.assignedPointSeries[0];
        }
    }

    async rodeoEventChange() {
        this.updatePayouts();
        await this.reloadPointStats();
    }

    updatePayouts() {
        const rodeoEventPayoutConfigs = this.rodeo.payoutConfigs.filter(pg => pg.sources.some(s => s.rodeoEventId === this.selectedRodeoEvent.id));
        this.payoutSelections = [{ name: 'All', id: 0 } as PayoutConfig, ...rodeoEventPayoutConfigs];
        this.selectedPayout = this.payoutSelections[0];
    }

    async reloadPointStats() {
        const rodeoEventId = this.selectedRodeoEvent.id;
        const payoutConfigId = this.selectedPayout.id;
        const pointSeriesId = this.selectedPointSeries ? this.selectedPointSeries.id : 0;
        const loading = this.loadingController.create({
            content: 'Loading point stats'
        })
        loading.present();
        this.pointStats = await this.pointStatsApiService
            .getRodeoPointStats(
                this.rodeo.id,
                rodeoEventId,
                0,
                payoutConfigId,
                pointSeriesId,
            );
        this.runsWithoutPoints = await this.pointStatsApiService
            .getRodeoRunsWithoutPoints(
                this.rodeo.id,
                rodeoEventId,
                payoutConfigId,
                pointSeriesId,
            );
        this.groupedPointStats = groupBy(this.pointStats, ({ leaderCategory }) => leaderCategory);
        await loading.dismiss();
        if (this.printing) {
            setTimeout(() => {
                window.document.title = this.rodeo.name + " - " + 'Points';
                window.print();
                this.navController.pop();
            }, 0);
        }
    }

    idOptionSorter(a, b) {
        return a.id == b.id;
    }

    matchSelections(a, b) {
        return a.name == b.name;
    }

    onSelectedPayoutChange() {
        this.loadPointSeries();
        this.reloadPointStats();
    }

    download() {
        const fileName = `${this.rodeo.name} - ${this.selectedRodeoEvent.displayName} - ${this.selectedPayout.name}`
        const secondColumnName = this.secondLabel;
        const flattenedPointStats = this.pointStats.map(ps => (
            {
                ...ps,
                'rider': ps.contestantOne.firstName + ' ' + ps.contestantOne.lastName,
                [secondColumnName]: this.getSecondValue(this.selectedRodeoEvent, ps),
            }
        ))
        const columns = ['leaderCategory', 'place', 'runNumber', 'rider', secondColumnName, 'scoreTimeValue', 'points'];
        this.reportService.exportToCsv(flattenedPointStats, fileName, columns, true)
        if (this.includeRunsWithoutPoints) {
            const fileName = `${this.rodeo.name} - ${this.selectedRodeoEvent.displayName} - ${this.selectedPayout.name}-nopoints`
            const secondColumnName = this.secondLabel;
            const flattenedPointStats = this.runsWithoutPoints.map(ps => (
                {
                    ...ps,
                    'rider': ps.contestantOne.firstName + ' ' + ps.contestantOne.lastName,
                    [secondColumnName]: this.getSecondValue(this.selectedRodeoEvent, ps),
                    'status': ps.contestantOne.hasMembership ? 'Is Member: Yes' : 'is Member: No',
                    'scoreTimeValue': ps.noTime ? 'NT*' : ps.scoreTimeValue,
                }
            ))
            const columns = ['leaderCategory', 'runNumber', 'rider', 'status', secondColumnName, 'scoreTimeValue',];
            this.reportService.exportToCsv(flattenedPointStats, fileName, columns, true)
        }
    }

    print() {
        this.navController.push(PointStatsReportComponent, {
            rodeo: this.rodeo,
            print: true,
            selectedRodeoEvent: this.selectedRodeoEvent,
            selectedPayout: this.selectedPayout,
            selectedPointSeries: this.selectedPointSeries,
            includeRunsWithoutPoints: this.includeRunsWithoutPoints,
        });
    }

    getSecondValue = getRunSecondValue;

    get isTeamEventType() {
        return this.selectedRodeoEvent.configuration.entries.showContestantTwo;
    }

    get firstLabel() {
        return this.selectedRodeoEvent.configuration.runs.slotOneLabel;
    }

    get secondLabel() {
        return this.selectedRodeoEvent.configuration.runs.slotTwoLabel;
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/reports/points/point-stats-report.component.ts